import React, { useState } from 'react';
import ProjectsAPI from '../api/projects';
import './ProjectSelector.css';
import { useSiteContext } from '../SiteContext';
import ActionButton from './ActionButton';
import { Project } from '../interfaces/Project';

interface ProjectSelectorProps {
    disabled?: boolean; // Optional disabled prop
    onProjectSelect?: (project: Project) => void; // Callback for project selection
    useSearchAll?: boolean; // Optional prop to use searchAll endpoint instead of search
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({ disabled, onProjectSelect, useSearchAll = false }) => {
    const { setLoading } = useSiteContext();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);

    const handleSearch = async () => {
        setLoading(true);
        const response: any = useSearchAll 
            ? await ProjectsAPI.searchAll(searchQuery)
            : await ProjectsAPI.search(searchQuery);
        setSearchResults(response.data);
        setLoading(false);
    };

    const handleSelectProject = (project: Project) => {
        setSelectedProject(project);
        setIsModalOpen(false);

        // Notify the parent component about the selected project
        if (onProjectSelect) {
            onProjectSelect(project);
        }
    };

    return (
        <div className="project-selector-container">
            <ActionButton 
                onAction={() => setIsModalOpen(true)} 
                isEnabled={!disabled} 
                title="Select Project"
            />

            {selectedProject && (
                <div className="selected-project">
                    Selected Project: {selectedProject.altProjectID} - {selectedProject.name}
                </div>
            )}

            {isModalOpen && (
                <>
                    {/* Overlay to dim the background */}
                    <div className="project-selector-overlay" onClick={() => setIsModalOpen(false)} />
                    
                    {/* Modal Content */}
                    <div className="project-selector-modal">
                        <h2>Select a Project</h2>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchQuery.trim() !== '') {
                                    handleSearch();
                                }
                            }}
                            placeholder="Search projects..."
                            className="project-selector-search-input"
                        />
                        <ActionButton onAction={handleSearch} isEnabled={searchQuery != ''} title="Search"/>

                        <ul className="project-selector-results">
                            {searchResults.map((project) => (
                                <li 
                                    key={project.projectID} 
                                    onClick={() => handleSelectProject(project)} 
                                    className="project-selector-result-item"
                                >
                                    {project.altProjectID} - {project.name}
                                </li>
                            ))}
                        </ul>

                        <ActionButton onAction={() => setIsModalOpen(false)} isEnabled={true} title="Close"/>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProjectSelector;
